import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import { siteConfig, categories, featuredVideos, popularArticles, faqItems, stats } from '@/mocks/homeData';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteConfig.name,
  url: siteConfig.url,
  description: siteConfig.description,
  logo: `${siteConfig.url}/logo.png`,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-6666',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
  sameAs: [
    'https://weibo.com/xiangrikui',
    'https://www.zhihu.com/org/xiangrikui',
  ],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: siteConfig.name,
  url: siteConfig.url,
  description: siteConfig.description,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteConfig.url}/search?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden" aria-label={siteConfig.name}>
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20sunflower%20field%20at%20golden%20hour%20sunset%2C%20vibrant%20yellow%20flowers%20facing%20the%20sun%2C%20soft%20warm%20light%2C%20peaceful%20natural%20landscape%2C%20artistic%20photography%20with%20dreamy%20bokeh%20effect%2C%20gentle%20color%20gradient%20sky%2C%20no%20text%2C%20clean%20composition&width=1800&height=900&seq=hero-bg-home-01&orientation=landscape"
              alt={siteConfig.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
            <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 font-heading leading-tight">
                {siteConfig.name}
              </h1>
              <p className="text-base md:text-xl text-white/85 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed">
                {siteConfig.tagline} — {siteConfig.description.slice(0, 80)}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/all"
                  className="whitespace-nowrap px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all duration-300 no-underline cursor-pointer hover:scale-105 transform"
                >
                  立即观看
                </Link>
                <Link
                  to="/about"
                  className="whitespace-nowrap px-8 py-3 rounded-full bg-white/15 text-white border border-white/30 font-medium text-sm hover:bg-white/25 transition-all duration-300 no-underline cursor-pointer backdrop-blur-sm"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="relative -mt-10 z-20" aria-label="平台数据">
            <div className="max-w-[1100px] mx-auto px-4 md:px-6">
              <div className="bg-background-50 rounded-xl border border-background-200/60 py-6 px-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="py-14 md:py-20" aria-label="视频分类">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">探索视频分类</h2>
                <p className="text-sm text-foreground-500 mt-3 max-w-lg mx-auto">
                  精选水果植物视频内容，涵盖香蕉、西瓜、蜜桃等多个品类，总有一款适合你
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {categories.filter(c => c.slug !== 'all').slice(0, 7).map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-3 p-6 rounded-xl bg-background-50 border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/40 transition-all duration-300 no-underline cursor-pointer"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors">
                      <i className={`${cat.icon} text-2xl text-accent-600`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap font-heading">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Videos */}
          <section className="py-14 md:py-20 bg-background-100" aria-label="精选视频">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">精选视频推荐</h2>
                  <p className="text-sm text-foreground-500 mt-2">最受欢迎的水果植物视频，每日更新</p>
                </div>
                <Link
                  to="/category/all"
                  className="whitespace-nowrap flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors no-underline"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="true">
                {featuredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Banner */}
          <section className="relative py-16 md:py-24 overflow-hidden" aria-label="特色栏目">
            <img
              src="https://readdy.ai/api/search-image?query=Beautiful%20assortment%20of%20fresh%20colorful%20fruits%20arranged%20artistically%20on%20a%20dark%20wooden%20surface%2C%20warm%20dramatic%20lighting%2C%20food%20photography%2C%20rich%20textures%2C%20elegant%20composition%2C%20vibrant%20colors%20of%20banana%20peach%20watermelon%20and%20grapes&width=1800&height=500&seq=cta-banner-home&orientation=landscape"
              alt="水果植物视频"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
            <div className="relative z-10 max-w-[900px] mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 font-heading">
                发现更多精彩视频内容
              </h2>
              <p className="text-sm md:text-base text-white/80 mb-6 max-w-xl mx-auto">
                高清画质、流畅播放、海量内容 — 向日葵视频为您打造极致的水果植物视频观看体验
              </p>
              <Link
                to="/category/all"
                className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white text-foreground-900 font-medium text-sm hover:bg-background-100 transition-all duration-300 no-underline cursor-pointer hover:scale-105 transform"
              >
                <i className="ri-play-circle-line text-lg"></i>
                开始探索
              </Link>
            </div>
          </section>

          {/* Popular Articles */}
          <section className="py-14 md:py-20" aria-label="热门文章">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">热门文章精选</h2>
                  <p className="text-sm text-foreground-500 mt-2">深度水果知识科普，让您更懂水果</p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {popularArticles.map((article) => (
                  <ArticleCard key={article.id} {...article} />
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20 bg-background-100" aria-label="常见问题">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">常见问题</h2>
                <p className="text-sm text-foreground-500 mt-3">关于向日葵视频的常见疑问解答</p>
              </div>
              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-lg bg-background-50 border border-background-200/60 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <h3 className="text-sm font-medium text-foreground-800 pr-4 font-heading">{item.question}</h3>
                      <i
                        className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === index ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}